import { useState, useEffect, useRef } from 'react';
import { LayerProps } from '@deck.gl/core';
import { ClickedMarker } from '../streetview.type';
import { normalizeMarker } from '../utils';
import { HouseholdGroup } from '../../../types/global';
import { KeyCode } from '../../../utils/constants';

type ClickHandler = NonNullable<LayerProps['onClick']>;

export default function useMarkerTooltip() {
  const [clickedMarker, setClickedMarker] = useState<ClickedMarker | null>(null);
  // Last selected household dot. Unlike clickedMarker, it stays after the card closes
  const [selectedHouseholdCoordinates, setSelectedHouseholdCoordinates] = useState<HouseholdGroup['coordinates'] | null>(null);
  const markerRef = useRef<HTMLDialogElement>(null);
  const mapAreaRef = useRef<HTMLElement>(null);
  const pressTarget = useRef<EventTarget | null>(null);

  useEffect(() => {
    const handlePress = (event: PointerEvent) => {
      const { target } = event;
      pressTarget.current = target;
      // Close card on press outside the map area
      if (!(target instanceof Node)) return;
      const isInApp = Boolean(document.getElementById('root')?.contains(target));
      if (isInApp && !mapAreaRef.current?.contains(target)) setClickedMarker(null);
    };
    document.addEventListener('pointerdown', handlePress, true);
    return () => document.removeEventListener('pointerdown', handlePress, true);
  }, []);

  const isInsideCard = () => {
    const target = pressTarget.current;
    return target instanceof Node && Boolean(markerRef.current?.contains(target));
  };

  const handleMarkerClick: ClickHandler = (info, event) => {
    if (isInsideCard()) return;
    if (info.object) {
      const marker = normalizeMarker(info);
      if (marker?.kind === 'household') setSelectedHouseholdCoordinates(marker.object.coordinates);
      const { x, y, viewport } = info;
      setClickedMarker(marker && {
        ...marker,
        shouldOpenLeft: x > (viewport?.width ?? 0) / 2,
        shouldOpenUp: y > (viewport?.height ?? 0) / 2,
      });
      if (event) event.stopPropagation();
    } else {
      setClickedMarker(null);
    }
  };

  // Clicking the map away from a marker closes the card.
  const handleMapClick: ClickHandler = (info) => {
    if (info.object || isInsideCard()) return;
    setClickedMarker(null);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (clickedMarker && (e.key as KeyCode) === KeyCode.Escape) {
        setClickedMarker(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [clickedMarker, markerRef]);

  const openMarkerObject = clickedMarker?.object;
  const wasCardOpen = useRef(false);

  useEffect(() => {
    if (openMarkerObject) {
      markerRef.current?.focus();
    } else if (wasCardOpen.current && document.activeElement === document.body) {
      mapAreaRef.current?.focus();
    }
    wasCardOpen.current = Boolean(openMarkerObject);
  }, [openMarkerObject]);

  return {
    clickedMarker, setClickedMarker, selectedHouseholdCoordinates, markerRef, mapAreaRef, handleMarkerClick, handleMapClick,
  };
}
